import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { levelsService } from '../services/levelsService';
import { LevelQuery, CreateLevelRequest, UpdateLevelRequest } from '../types';
import { toast } from 'sonner';

const QUERY_KEY = 'levels';

/**
 * Hook to fetch all levels with pagination and filters
 */
export const useLevels = (params?: LevelQuery) => {
  return useQuery({
    queryKey: [QUERY_KEY, params],
    queryFn: () => levelsService.getAll(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch a single level by ID
 */
export const useLevel = (id: string | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => levelsService.getById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch active levels only
 */
export const useActiveLevels = () => {
  return useQuery({
    queryKey: [QUERY_KEY, 'active'],
    queryFn: () => levelsService.getActive(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch levels for dropdown (public - no permissions required)
 * Returns only id and name for use in select/dropdown components
 */
export const useDropdownLevels = () => {
  return useQuery({
    queryKey: [QUERY_KEY, 'dropdown'],
    queryFn: () => levelsService.getDropdown(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to create a new level
 */
export const useCreateLevel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLevelRequest) => levelsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Nível de acesso criado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao criar nível de acesso');
    },
  });
};

/**
 * Hook to update a level
 */
export const useUpdateLevel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLevelRequest }) =>
      levelsService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, variables.id] });
      toast.success('Nível de acesso atualizado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar nível de acesso');
    },
  });
};

/**
 * Hook to delete a level
 */
export const useDeleteLevel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => levelsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Nível de acesso excluído com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir nível de acesso');
    },
  });
};

/**
 * Hook to activate a level
 */
export const useActivateLevel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => levelsService.activate(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, id] });
      toast.success('Nível de acesso ativado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao ativar nível de acesso');
    },
  });
};

/**
 * Hook to deactivate a level
 */
export const useDeactivateLevel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => levelsService.deactivate(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, id] });
      toast.success('Nível de acesso desativado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao desativar nível de acesso');
    },
  });
};

/**
 * Hook to seed default levels
 */
export const useSeedDefaultLevels = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => levelsService.seedDefaults(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Níveis padrão criados com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao criar níveis padrão');
    },
  });
};
